import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { typedQueryClient, useTypedMutation, userKey } from "hooks";
import { isAppointmentInSelectedList } from "modules/Appointments/components/NewAppointment/utils";
import { AppointmentsQueryKeys } from "modules/Appointments/queryKeys";
import { ApiInstance } from "utils/Api";
import { updateAppointmentStatusIdentifier } from "utils/appointmentUtils";

import type { APPOINTMENT_RECALL_STATUSES, Appointment } from "models";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";
import type { CarSection, CustomerSection } from "modules/Appointments/components/NewAppointment";

export type CreateNewAppointmentPayload = {
  wo_nr: string;
  dms_nr: string;
  internal: boolean;
  time_car_app: string;
  due_in: string | null;
  is_lease_company: boolean;
  customer_waiting: boolean;
  car_in_shop: string | null;
  dealer_location_id: number;
  car_return_time: string | null;
  has_recalls_data: APPOINTMENT_RECALL_STATUSES;
  customer_owner: CustomerSection | null;
  customer_driver: CustomerSection | null;
  customer_contract: CustomerSection | null;
  car?: CarSection;
  interventions?: AddInterventionPayload[];
};

type CreateNewAppointmentResponse = {
  appointment: Appointment;
  appointment_id: number;
};

export const useNewAppointmentMutation = () => {
  const { t } = useTranslation();

  return useTypedMutation<CreateNewAppointmentPayload, CreateNewAppointmentResponse>({
    mutationFn: async payload => (await ApiInstance.post("/appointments/create", payload)).data,
    onSuccess: (_, response) => {
      toast.success(t("v8_new_appointment_created_successfully", "New appointment created successfully"));

      const appointments = typedQueryClient.getQueryData(AppointmentsQueryKeys.list) ?? [];
      const selectedDates = typedQueryClient.getQueryData(AppointmentsQueryKeys.selectedDatesToList);
      const selectedDate = typedQueryClient.getQueryData(AppointmentsQueryKeys.selectedDateToList) ?? new Date();
      const listByScheduledRange = Boolean(typedQueryClient.getQueryData(userKey)?.list_appointments_by_scheduled_range);
      const appointment = updateAppointmentStatusIdentifier(response.appointment);

      const appointmentIdx = appointments.findIndex(a => a.id === response.appointment.id);
      if (appointmentIdx >= 0) return;

      if (isAppointmentInSelectedList({ appointment, selectedDate, ...(selectedDates ?? {}), listByScheduledRange }))
        typedQueryClient.setQueryData(AppointmentsQueryKeys.list, [...appointments, appointment]);
    },
    onError: () => toast.error(t("v8_error_while_creating_appointment", "Error while creating the appointment"))
  });
};
